<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { MpContextMenu, MpInput, MpRadio, MpText, openMpConfirmModal } from '@borg/ui';

  interface CoverLetter {
    id: number;
    content: string;
    title: string;
    createdAt: Date;
  }

  const props = defineProps<{
    letters: CoverLetter[];
    selectedLetter?: string | null;
  }>();

  const emit = defineEmits<{
    select: [letterId: string, letterContent: string];
    rename: [letterEntityId: number, newName: string];
    delete: [letterEntityId: number];
  }>();

  const { t } = useI18n();

  const selectedLetterModel = computed({
    get: () => props.selectedLetter,
    set: (value) => {
      if (value !== null && value !== undefined) {
        const index = parseInt(value);
        const letter = props.letters[index];
        if (letter) {
          emit('select', value, letter.content);
        }
      }
    },
  });

  const isRenameModalOpen = ref(false);
  const letterToRename = ref<{ id: string; title: string } | null>(null);
  const newLetterName = ref('');

  function getLetterPreview(content: unknown): string {
    if (!content || typeof content !== 'string') {
      return 'Preview not available';
    }
    const preview = content.replaceAll('\n', ' ').substring(0, 100);
    return preview + (content.length > 100 ? '...' : '');
  }

  function handleSelect(index: number, letter: CoverLetter) {
    emit('select', index.toString(), letter.content);
  }

  function openRenameModal(index: number, letter: CoverLetter) {
    letterToRename.value = { id: letter.id.toString(), title: letter.title };
    newLetterName.value = letter.title;
    isRenameModalOpen.value = true;
  }

  function confirmRename() {
    if (letterToRename.value && newLetterName.value.trim()) {
      emit('rename', parseInt(letterToRename.value.id), newLetterName.value);
      isRenameModalOpen.value = false;
      letterToRename.value = null;
      newLetterName.value = '';
    }
  }

  async function handleDelete(letter: CoverLetter) {
    const isConfirmed = await openMpConfirmModal({
      title: t('JOB_APPLICATION_PAGE.DELETE_COVER_LETTER_MODAL.TITLE'),
      text: t('JOB_APPLICATION_PAGE.DELETE_COVER_LETTER_MODAL.TEXT'),
      confirmButtonLabel: t('YES'),
      cancelButtonLabel: t('NO'),
    });

    if (isConfirmed) {
      emit('delete', letter.id);
    }
  }
</script>

<template>
  <div class="cover-letters-list">
    <div
      v-for="(letter, index) in letters"
      :key="index"
      class="cover-letter-item"
    >
      <div class="cover-letter-item__content">
        <MpRadio
          v-model="selectedLetterModel"
          :value="index.toString()"
          name="coverLetter"
        />
        <div
          class="cover-letter-item__details"
          @click="handleSelect(index, letter)"
        >
          <MpText
            as="h6"
            no-margin
            no-wrap
            class="cover-letter-item__name"
          >
            {{ letter.title }}
          </MpText>
          <MpText
            type="body2"
            no-margin
            class="cover-letter-item__preview"
          >
            {{ getLetterPreview(letter.content) }}
          </MpText>
        </div>
      </div>
      <MpContextMenu
        :options="[
          { label: t('RENAME'), onClick: () => openRenameModal(index, letter) },
          { label: t('DELETE'), onClick: () => handleDelete(letter) },
        ]"
      />
    </div>
  </div>

  <SiteActionsModal
    :is-open="isRenameModalOpen"
    :title="t('JOB_APPLICATION_PAGE.RENAME_COVER_LETTER_MODAL.TITLE')"
    :in-progress="false"
    size="sm"
    @confirm="confirmRename"
    @cancel="isRenameModalOpen = false"
  >
    <MpInput
      v-model="newLetterName"
      :label="t('JOB_APPLICATION_PAGE.RENAME_COVER_LETTER_MODAL.NAME_LABEL')"
      :placeholder="t('JOB_APPLICATION_PAGE.RENAME_COVER_LETTER_MODAL.NAME_PLACEHOLDER')"
      required
    />
  </SiteActionsModal>
</template>

<style scoped lang="scss">
  .cover-letters-list {
    display: flex;
    flex-direction: column;
    gap: var(--mp-space-10);
  }

  .cover-letter-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--mp-space-50) 0;
    border-bottom: 1px solid var(--mp-color-border-c-10);
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }

    &:hover {
      background-color: var(--mp-color-background-hover);
    }

    &__content {
      display: flex;
      align-items: flex-start;
      gap: var(--mp-space-50);
      flex: 1;
    }

    &__details {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: var(--mp-space-20);
    }

    &__name {
      font-weight: 600;
      color: var(--mp-color-text-primary);
    }

    &__preview {
      color: var(--mp-color-text-secondary);
      line-height: 1.4;
    }
  }
</style>
